import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/helpers/toast';

import { useAuthenticatedMutation } from '@/hooks/auth/use-auth-query-hooks';
import { invalidateRelatedQueries } from '@/lib/query/cache-invalidation';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';

interface BulkInviteParams {
  accountId: string;
  emails: string[];
  roles: string[];
}

export function useBulkInviteUsers() {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation({
    mutationFn: async ({ accountId, emails, roles }: BulkInviteParams) => {
      if (emails.length !== roles.length) {
        throw new Error('The number of emails and roles must match');
      }

      if (emails.length > 10) {
        throw new Error('Maximum 10 invitations can be sent at once');
      }

      const response = await AccountsApiService.bulkInviteUsers(accountId, emails, roles);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success('Invitations have been sent successfully.');
      invalidateRelatedQueries(queryClient, 'TEAM_MEMBERS', { refetchType: 'active' });
      void queryClient.invalidateQueries({
        queryKey: ['teamMembers', variables.accountId],
        refetchType: 'active',
      });
    },
    onError: (error: any) => {
      console.error('Error sending bulk invitations:', error);
      toast.error(error?.response?.data?.message || 'Failed to send invitations.');
    },
  });
}
